/**
 * assets/journey-hosts.js - the host cards of the "Meet our hosts" page
 * (sections/journey-hosts.liquid; approved 2026-09-28).
 *
 * Reads GET https://www.glowming.business/api/journey-hosts once and draws one
 * card per host, exactly as the approved mock-up (hosts.py): photo or initial,
 * name, "Host · Glowming Journey", her pick with its pouch, headline, message,
 * the R55 row when she has the deal, her socials, and "Join with <name>".
 *
 * Every value from the endpoint is written as TEXT (textContent), never as
 * HTML, and every URL passes the same allow-lists the homepage host card uses
 * (window.GlowmingHomeHost from assets/home-host-card.js): the photo must be a
 * signed URL from our storage, socials one of the known sites, the join link
 * our own join page. On any failure the grid stays empty and the "being
 * approved" line shows; the rest of the page never depends on it.
 *
 * The two filter chips show all hosts or only hosts with the R55 deal.
 */
(function () {
  var JOIN_PREFIX = 'https://www.glowming.business/journey/join?gs_ref=';

  /** The pouch image for a pick handle: the flavour word decides. */
  function pouchFor(handle, grid) {
    var h = String(handle || '').toLowerCase();
    if (h.indexOf('matcha') !== -1) return { src: grid.getAttribute('data-pouch-matcha'), name: 'Milky Matcha' };
    if (h.indexOf('latte') !== -1) return { src: grid.getAttribute('data-pouch-latte'), name: 'Café Latte' };
    if (h.indexOf('pomegranate') !== -1) return { src: grid.getAttribute('data-pouch-pomegranate'), name: 'Pomegranate' };
    if (h.indexOf('choco') !== -1) return { src: grid.getAttribute('data-pouch-choco'), name: 'Choco' };
    return null;
  }

  /** A theme-editor label with {name} / {code} filled in. */
  function fill(label, values) {
    return String(label || '').replace(/\{(name|code)\}/g, function (_, key) { return values[key] || ''; });
  }

  /** document.createElement with a class and optional text. */
  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  }

  /** One host card, or null when the answer is not usable. */
  function card(host, grid, H) {
    if (!host || typeof host.displayName !== 'string' || typeof host.firstName !== 'string') return null;
    var join = H.safeUrl(host.joinUrl, [JOIN_PREFIX]);
    if (!join) return null;
    var article = el('article', 'gj-hosts-card');
    if (host.r55Code) article.setAttribute('data-r55', 'true');

    var top = el('div', 'gj-hosts-card__top');
    var photo = H.safeUrl(host.photoUrl, [H.PHOTO_PREFIX]);
    if (photo) {
      var img = el('img', 'gj-hosts-card__photo');
      img.src = photo;
      img.alt = '';
      img.loading = 'lazy';
      top.appendChild(img);
    } else {
      var avatar = el('span', 'gj-hosts-card__avatar', host.firstName.charAt(0).toUpperCase());
      avatar.setAttribute('aria-hidden', 'true');
      top.appendChild(avatar);
    }
    var who = el('div', 'gj-hosts-card__who');
    who.appendChild(el('span', 'gj-hosts-card__name', host.displayName));
    who.appendChild(el('span', 'gj-eyebrow', grid.getAttribute('data-host-label')));
    top.appendChild(who);
    article.appendChild(top);

    var pouch = pouchFor(host.productHandle, grid);
    if (pouch && pouch.src) {
      var pick = el('div', 'gj-hosts-card__pick');
      var pImg = el('img');
      pImg.src = pouch.src;
      pImg.alt = pouch.name + ' pouch';
      pImg.loading = 'lazy';
      pick.appendChild(pImg);
      var pText = el('span', null, grid.getAttribute('data-pick-label'));
      pText.appendChild(el('strong', null, pouch.name));
      pick.appendChild(pText);
      article.appendChild(pick);
    }

    if (host.headline) article.appendChild(el('h3', 'gj-h3', host.headline));
    if (host.message) article.appendChild(el('p', 'gj-serif gj-hosts-card__msg', '“' + host.message + '”'));

    if (host.r55Code) {
      var r55 = el('div', 'gj-hosts-r55');
      r55.appendChild(el('span', 'gj-hosts-r55__amt', grid.getAttribute('data-r55-amount')));
      var line = el('span');
      var parts = String(grid.getAttribute('data-r55-text') || '').split('{code}');
      line.appendChild(document.createTextNode(fill(parts[0], { name: host.firstName })));
      if (parts.length > 1) {
        line.appendChild(el('strong', null, host.r55Code));
        line.appendChild(document.createTextNode(fill(parts.slice(1).join(''), { name: host.firstName })));
      }
      r55.appendChild(line);
      article.appendChild(r55);
    }

    var socials = H.socialsFrom(host.socials);
    if (socials.length) {
      var row = el('div', 'gj-hosts-socials');
      socials.forEach(function (s) {
        var a = el('a', 'gj-hosts-soc');
        a.href = s.url;
        a.target = '_blank';
        a.rel = 'noopener';
        a.setAttribute('aria-label', host.firstName + ' on ' + s.name);
        a.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true">' + H.ICONS[s.key] + '</svg>';
        row.appendChild(a);
      });
      article.appendChild(row);
    }

    var btn = el('a', 'gj-btn gj-btn--matcha', fill(grid.getAttribute('data-join-label'), { name: host.firstName }) + ' →');
    btn.href = join;
    article.appendChild(btn);
    return article;
  }

  /** Show all hosts, or only those with the R55 deal. */
  function bindFilters(grid) {
    var chips = document.querySelectorAll('[data-hosts-filter]');
    /* Hosts exist but the filter hides them all: say so (Codex r1), never a blank grid. */
    var none = document.querySelector('[data-journey-hosts-filter-empty]');
    Array.prototype.forEach.call(chips, function (chip) {
      chip.addEventListener('click', function () {
        var only = chip.getAttribute('data-hosts-filter') === 'r55';
        var shown = 0;
        Array.prototype.forEach.call(chips, function (c) { c.setAttribute('aria-pressed', String(c === chip)); });
        Array.prototype.forEach.call(grid.children, function (c) {
          c.hidden = only && c.getAttribute('data-r55') !== 'true';
          if (!c.hidden) shown += 1;
        });
        if (none) none.hidden = shown > 0 || grid.children.length === 0;
      });
    });
  }

  function start() {
    var grid = document.querySelector('[data-journey-hosts]');
    var H = window.GlowmingHomeHost;
    if (!grid || !H) return;
    var empty = document.querySelector('[data-journey-hosts-empty]');
    var showEmpty = function () { if (empty) empty.hidden = grid.children.length > 0; };
    fetch(grid.getAttribute('data-endpoint'), { mode: 'cors', credentials: 'omit', headers: { Accept: 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (body) {
        var hosts = body && body.status === 'ok' && Array.isArray(body.hosts) ? body.hosts : [];
        hosts.forEach(function (h) { var c = card(h, grid, H); if (c) grid.appendChild(c); });
        bindFilters(grid);
        showEmpty();
      })
      .catch(showEmpty);
  }

  window.GlowmingJourneyHosts = { pouchFor: pouchFor, fill: fill, bindFilters: bindFilters };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
